﻿// BINDINGS

import type { DisplayText } from "@/components/platform/common/displayText";
import type {
  LabelledConfiguration,
  LabellingControlProps,
} from "@/components/platform/common/labelling";
import type { Ref, VNode } from "vue";
import type { EnumLocaleAwareField } from "@/app/services/extensions/localisationServiceExtended";
import { getLocaleAwareRule } from "@/components/composables/localisation";
import type { Location } from "vue-router";

export const isTwoWayDataBinding = (
  value: string | TwoWayDataBinding<string>
): value is TwoWayDataBinding<string> => {
  return (value as TwoWayDataBinding<string>).get !== undefined;
};

export interface TwoWayDataBinding<T> {
  get: () => T;
  set: (value: T) => void;
}

export interface Bindable<T> {
  value: TwoWayDataBinding<T>;
}

export function bindTo<T extends object, K extends keyof T>(
  sourceObject: T,
  propName: K
): TwoWayDataBinding<T[K]> {
  return {
    get: () => sourceObject[propName],
    set: (value) => (sourceObject[propName] = value),
  };
}

export function bindToFunctions<T>(
  getFunc: () => T,
  setFunc: (value: T) => void
): TwoWayDataBinding<T> {
  return {
    get: () => getFunc(),
    set: (value) => setFunc(value),
  };
}

export function placeholderBinding(): TwoWayDataBinding<any> {
  return {
    get: () => undefined,
    set: () => undefined,
  };
}

export function bindToRef<T>(source: Ref<T>): TwoWayDataBinding<T> {
  return {
    get: () => source.value,
    set: (value) => (source.value = value),
  };
}

export const isComponentVisible = (
  configuration: BaseInputConfiguration<any>
): boolean => {
  if (configuration.localeAwareField) {
    if (
      !(
        getLocaleAwareRule(configuration.localeAwareField)?.isApplicable ?? true
      )
    ) {
      return false;
    }
  }

  if (configuration.isVisible === undefined) {
    return true;
  }
  return configuration.isVisible instanceof Function
    ? configuration.isVisible()
    : configuration.isVisible;
};

export function renderInputComponentIfVisible(
  configuration: BaseInputConfiguration<any>,
  renderFn: (...args: any[]) => VNode
) {
  if (isComponentVisible(configuration)) {
    return renderFn();
  }
}

//#region CAPABILITIES

export interface Hideable {
  isVisible?: boolean | (() => boolean);
}

export type ValidationRule<T> = (value?: T) => boolean | string;

export interface Validatable<T> {
  rules?: ValidationRule<T>[] | (() => ValidationRule<T>[]);
}

export interface Clearable {
  clearable?: boolean;
}

export interface Countable {
  counter?: boolean | number | string;
}

export interface Suffixable {
  suffix?: DisplayText;
}

export interface Prefixable {
  prefix?: DisplayText;
}

export interface Integral {
  isIntegral?: boolean;
}

export interface Readonly {
  readOnly?: boolean | (() => boolean);
}

export interface ValidateOnBlur {
  validateOnBlur?: boolean;
}

export interface DefaultValue {
  defaultValue?: null | undefined;
}

export interface Placeholder {
  placeholder?: DisplayText;
}

export interface Quantisable {
  quantise?: boolean;
}

export interface TextInputType {
  type?: "password";
}

export interface Routeable {
  routerLocation?: Location;
}

export interface ImageWidth {
  imageWidth?: number;
}

export interface MaxFileMBytes {
  maxFileMBytes?: number;
}

export interface AutoTrim {
  autoTrim?: boolean;
}

export interface LocaleAware {
  localeAwareField?: EnumLocaleAwareField;
}

export interface MinimumValue {
  min?: number;
}

export interface MaximumValue {
  max?: number;
}
//#endregion

//#region INPUT CONFIGURATION

export type BaseInputConfiguration<T> = LabelledConfiguration &
  Hideable &
  Validatable<T> &
  Readonly &
  LocaleAware &
  Routeable;

//#endregion

//#region CONTROL PROPS

export interface BaseControlProps<T> {
  value?: T;
  rules?: ValidationRule<T>[];
  readOnly?: boolean | (() => boolean);
  localeAwareField?: EnumLocaleAwareField;
  routerLocation?: Location;
}

export type ControlAttrs<T> = LabellingControlProps & T;

//#endregion
